import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { api } from "../../api";
import { IoGlobe, IoRocketSharp, IoAdd, IoCodeSlash, IoTrashOutline } from "react-icons/io5";

const darkInputSx = {
  backgroundColor: "#0b1437 !important",
  borderRadius: "8px",
  border: "1px solid rgba(255, 255, 255, 0.25) !important",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "transparent !important",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4) !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0075ff !important",
    },
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
  },
  "& .MuiInputBase-input": {
    color: "#ffffff !important",
    backgroundColor: "transparent !important",
    fontSize: "0.875rem",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5) !important",
      opacity: "1 !important",
    },
    "&::-webkit-input-placeholder": {
      color: "rgba(255, 255, 255, 0.5) !important",
    },
  },
  "& .MuiSelect-select": {
    color: "#ffffff !important",
    backgroundColor: "#0b1437 !important",
  },
  "& .MuiSvgIcon-root": {
    color: "rgba(255, 255, 255, 0.7) !important",
  },
};

export default function Sites() {
  const history = useHistory();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ghRepos, setGhRepos] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    repoUrl: "",
    repoName: "",
    branch: "main",
    rootDir: "/",
    buildCmd: "",
    publishDir: "dist",
  });

  const loadSites = async () => {
    try {
      const res = await api.listSites();
      if (res.success && res.data) {
        setSites(res.data.sites);
      }
    } catch (err) {
      console.error("Failed to load sites:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadGitHubRepos = async () => {
    try {
      const statusRes = await api.getGitHubStatus();
      if (statusRes.success && statusRes.data?.connected) {
        const repoRes = await api.getGitHubRepos();
        if (repoRes.success && repoRes.data?.repos) {
          setGhRepos(repoRes.data.repos);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadSites();
    loadGitHubRepos();
    const interval = setInterval(loadSites, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleNameChange = (e) => {
    const val = e.target.value;
    const slug = val
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 32);
    setFormData((prev) => ({
      ...prev,
      name: val,
      subdomain: prev.subdomain ? prev.subdomain : slug,
    }));
  };

  const handleRepoSelect = (repoFullName) => {
    const repo = ghRepos.find((r) => r.fullName === repoFullName);
    if (repo) {
      setFormData((prev) => ({
        ...prev,
        repoName: repo.fullName,
        repoUrl: repo.htmlUrl,
        branch: repo.defaultBranch || "main",
        name: prev.name || repo.name,
        subdomain: prev.subdomain || repo.name.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
      }));
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.subdomain || !formData.repoUrl) {
      setError("Site name, subdomain, and Git repository URL are required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.createSite(formData);
      if (res.success && res.data?.siteId) {
        setModalOpen(false);
        setFormData({
          name: "",
          subdomain: "",
          repoUrl: "",
          repoName: "",
          branch: "main",
          rootDir: "/",
          buildCmd: "",
          publishDir: "dist",
        });
        history.push(`/sites/${res.data.siteId}`);
      }
    } catch (err) {
      setError(err.message || "Failed to create site");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSite = async (siteId, siteName) => {
    if (!window.confirm(`Are you sure you want to delete "${siteName}"? This will permanently remove its releases and stop hosting.`)) {
      return;
    }
    try {
      await api.deleteSite(siteId);
      loadSites();
    } catch (err) {
      alert("Failed to delete site: " + err.message);
    }
  };

  const handleRedeploy = async (siteId) => {
    try {
      await api.redeploySite(siteId);
      alert("Redeployment queued!");
      loadSites();
    } catch (err) {
      alert("Failed to trigger redeployment: " + err.message);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h4" color="white" fontWeight="bold">
              Hosted Sites
            </VuiTypography>
            <VuiTypography variant="button" color="text" fontWeight="regular">
              Manage your static websites, custom subdomains, and deployments
            </VuiTypography>
          </VuiBox>
          <VuiButton variant="gradient" color="info" onClick={() => setModalOpen(true)}>
            <IoAdd size="18px" style={{ marginRight: "6px" }} />
            New Site
          </VuiButton>
        </VuiBox>

        {/* Sites Grid */}
        {loading ? (
          <VuiBox display="flex" justifyContent="center" py={8}>
            <CircularProgress color="info" />
          </VuiBox>
        ) : sites.length === 0 ? (
          <Card>
            <VuiBox py={8} textAlign="center">
              <IoGlobe size="48px" color="#0075ff" style={{ marginBottom: "16px" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                No Websites Deployed Yet
              </VuiTypography>
              <VuiTypography variant="button" color="text" display="block" mb={3} sx={{ maxWidth: "460px", margin: "0 auto 20px" }}>
                Connect a GitHub repository or paste any public Git URL to automatically build and host your first static web application.
              </VuiTypography>
              <VuiButton variant="gradient" color="info" onClick={() => setModalOpen(true)}>
                Deploy First Site &rarr;
              </VuiButton>
            </VuiBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {sites.map((site) => (
              <Grid item xs={12} md={6} lg={4} key={site.id}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  <VuiBox>
                    {/* Top Row: Icon + Status */}
                    <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <VuiBox display="flex" alignItems="center" gap={1.5}>
                        <VuiBox
                          width="42px"
                          height="42px"
                          borderRadius="12px"
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <IoGlobe color="#fff" size="22px" />
                        </VuiBox>
                        <VuiBox>
                          <VuiTypography variant="h6" color="white" fontWeight="bold">
                            {site.name}
                          </VuiTypography>
                          <VuiTypography variant="caption" color="text">
                            branch: <strong>{site.branch}</strong>
                          </VuiTypography>
                        </VuiBox>
                      </VuiBox>
                      <VuiBadge
                        color={site.status === "live" ? "success" : site.status === "building" ? "info" : site.status === "failed" ? "error" : "warning"}
                        variant="standard"
                        size="sm"
                        badgeContent={site.status.toUpperCase()}
                      />
                    </VuiBox>

                    {/* Subdomain Link */}
                    <VuiBox mb={2} p={1.5} sx={{ backgroundColor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                      <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                        {site.status === "live" ? "LIVE URL" : site.status === "building" ? "BUILD IN PROGRESS" : "STATUS / URL"}
                      </VuiTypography>
                      {site.status === "live" ? (
                        <a href={site.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                          <VuiTypography variant="button" color="info" fontWeight="bold" sx={{ wordBreak: "break-all" }}>
                            {site.subdomain}.web.int.yt ↗
                          </VuiTypography>
                        </a>
                      ) : site.status === "failed" ? (
                        <VuiTypography
                          variant="button"
                          color="error"
                          fontWeight="bold"
                          sx={{ wordBreak: "break-all", cursor: "pointer" }}
                          onClick={() => history.push(`/sites/${site.id}`)}
                        >
                          Build Failed — View Logs & Fix ↗
                        </VuiTypography>
                      ) : (
                        <VuiTypography variant="button" color="warning" sx={{ wordBreak: "break-all" }}>
                          {site.subdomain}.web.int.yt (Provisioning...)
                        </VuiTypography>
                      )}
                    </VuiBox>

                    {/* Repo details */}
                    <VuiBox mb={2}>
                      <VuiTypography variant="caption" color="text" display="flex" alignItems="center" gap={1}>
                        <IoCodeSlash size="14px" />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {site.repoUrl}
                        </span>
                      </VuiTypography>
                      <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                        Last deployed: {site.lastDeployedAt ? new Date(site.lastDeployedAt).toLocaleString() : "Pending initial build"}
                      </VuiTypography>
                    </VuiBox>
                  </VuiBox>

                  {/* Actions Footer */}
                  <VuiBox pt={2} borderTop="1px solid rgba(255,255,255,0.08)" display="flex" justifyContent="space-between" alignItems="center">
                    <VuiBox display="flex" gap={1}>
                      <VuiButton
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={() => history.push(`/sites/${site.id}`)}
                      >
                        Manage
                      </VuiButton>
                      <VuiButton
                        variant="outlined"
                        color="white"
                        size="small"
                        onClick={() => handleRedeploy(site.id)}
                      >
                        Redeploy
                      </VuiButton>
                    </VuiBox>
                    <VuiButton
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteSite(site.id, site.name)}
                    >
                      <IoTrashOutline size="16px" />
                    </VuiButton>
                  </VuiBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </VuiBox>

      {/* New Site Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => !submitting && setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#08102b",
            backgroundImage: "none",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "16px",
            color: "#fff",
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff", fontWeight: "bold" }}>
          Deploy a New Static Website
        </DialogTitle>
        <form onSubmit={handleCreateSite}>
          <DialogContent dividers sx={{ borderColor: "rgba(255,255,255,0.08)" }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, backgroundColor: "rgba(244,67,54,0.15)", color: "#ff8a80" }}>
                {error}
              </Alert>
            )}

            {/* GitHub Repo Selector (if connected) */}
            {ghRepos.length > 0 && (
              <VuiBox mb={2.5}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  SELECT FROM CONNECTED GITHUB REPOSITORIES (OPTIONAL)
                </VuiTypography>
                <TextField
                  select
                  fullWidth
                  value={formData.repoName}
                  onChange={(e) => handleRepoSelect(e.target.value)}
                  size="small"
                  sx={darkInputSx}
                >
                  <MenuItem value="">-- Select a repository or type URL below --</MenuItem>
                  {ghRepos.map((r) => (
                    <MenuItem key={r.id} value={r.fullName}>
                      {r.fullName} ({r.defaultBranch})
                    </MenuItem>
                  ))}
                </TextField>
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Quickly auto-fill repository configuration from your connected GitHub account
                </VuiTypography>
              </VuiBox>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  SITE NAME *
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="e.g. My Portfolio"
                  value={formData.name}
                  onChange={handleNameChange}
                  size="small"
                  required
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Friendly label for this site in your dashboard
                </VuiTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  SUBDOMAIN *
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="e.g. my-portfolio"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  size="small"
                  required
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="info" fontSize="0.75rem" mt={0.5} display="block">
                  Your live free URL: https://{formData.subdomain || "your-site"}.web.int.yt
                </VuiTypography>
              </Grid>

              <Grid item xs={12} sm={8}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  GIT REPOSITORY URL *
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="https://github.com/username/repository"
                  value={formData.repoUrl}
                  onChange={(e) => setFormData({ ...formData, repoUrl: e.target.value })}
                  size="small"
                  required
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Public or private GitHub repo URL (e.g. https://github.com/tastejs/todomvc)
                </VuiTypography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  BRANCH *
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="main"
                  value={formData.branch}
                  onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                  size="small"
                  required
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Git branch to build and deploy
                </VuiTypography>
              </Grid>

              <Grid item xs={12} sm={4}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  ROOT DIRECTORY
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="/"
                  value={formData.rootDir}
                  onChange={(e) => setFormData({ ...formData, rootDir: e.target.value })}
                  size="small"
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Subfolder in repo (leave / if at root)
                </VuiTypography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  BUILD COMMAND
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="e.g. npm run build"
                  value={formData.buildCmd}
                  onChange={(e) => setFormData({ ...formData, buildCmd: e.target.value })}
                  size="small"
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Build command (or leave empty if static HTML)
                </VuiTypography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                  PUBLISH DIRECTORY *
                </VuiTypography>
                <TextField
                  fullWidth
                  placeholder="dist, build, or ."
                  value={formData.publishDir}
                  onChange={(e) => setFormData({ ...formData, publishDir: e.target.value })}
                  size="small"
                  required
                  sx={darkInputSx}
                />
                <VuiTypography variant="caption" color="text" fontSize="0.75rem" mt={0.5} display="block">
                  Output folder containing index.html
                </VuiTypography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <VuiButton color="white" variant="outlined" onClick={() => setModalOpen(false)} disabled={submitting}>
              Cancel
            </VuiButton>
            <VuiButton type="submit" color="info" variant="gradient" disabled={submitting}>
              {submitting ? "Deploying..." : "Create & Deploy Site"}
            </VuiButton>
          </DialogActions>
        </form>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
